import { FC, useContext, useEffect, useState } from "react";

import { Stack } from "capitalrx-components-2.0/lib/layout";
import { Body, Semibold } from "capitalrx-components-2.0/lib/typography";
import { Link, Spinner } from "capitalrx-components-2.0/lib/general";
import { CheckboxValue } from "capitalrx-components-2.0/lib/forms";

import { getPACaseList } from "@/api";
import { AuthenticationContext, ThemeSelectorContext } from "@/context";
import { PACaseFilterBar } from "./PACaseFilterBar";

import styles from "./PAListForm.module.scss";
import PAResultList from "./PAResultList";

export const PAListForm: FC = () => {
  const { getAuthToken } = useContext(AuthenticationContext);
  const { primary_color } = useContext(ThemeSelectorContext);

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [caseFilters, setCaseFilters] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageNumber, setPageNumber] = useState(0);
  const [actionRequired, setActionRequired] = useState<CheckboxValue>(false);

  const toggleCaseFilter = (status: string) => {
    setCaseFilters((prev) =>
      prev.includes(status) ? [] : [status],
    );
  };

  useEffect(() => {
    const fetchCases = async () => {
      setLoading(true);
      try {
        const authToken = getAuthToken();

        const requestPayload = {
          is_action_required: !!actionRequired,
          case_outcome: actionRequired ? [] : caseFilters,
          query_term: searchTerm || "",
          results_per_page: 10,
          page_number: pageNumber,
        };

        const response = await getPACaseList(requestPayload, authToken);
        setResults(response?.results || []);
      } catch (error) {
        console.error("Failed to fetch cases:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchCases();
  }, [caseFilters, actionRequired, searchTerm, pageNumber]);

  useEffect(() => {
    if (actionRequired) {
      setCaseFilters([]);
    }
  }, [actionRequired]);

  return (
    <div className={styles.CaseSearchContainer}>
      <Stack spacing="small">
        <PACaseFilterBar
          searchTerm={searchTerm}
          onSearchChange={setSearchTerm}
          selectedStatuses={caseFilters}
          onStatusToggle={toggleCaseFilter}
          actionRequired={actionRequired}
          onActionRequiredChange={setActionRequired}
          searchInstructions="Search by patient name, case ID, member ID, drug name, or date"
        />
        {loading && (
          <Stack alignment="center">
            <Spinner size="large" />
          </Stack>
        )}
        {!loading && results?.length === 0 && searchTerm && (
          <div className={styles.ResultPanel}>
            <Stack spacing="small">
              <Semibold>No Cases Found</Semibold>
              <Link style={{ color: primary_color }} to="" onClick={() => setSearchTerm("")}>
                Clear Search
              </Link>
              {caseFilters.length > 0 && (
                <Link style={{ color: primary_color }} to="" onClick={() => setCaseFilters([])}>Expand search to all statuses</Link>
              )}
              {actionRequired && (
                <Link style={{ color: primary_color }} to="" onClick={() => setActionRequired(false)}>Expand search to all statuses</Link>
              )}
            </Stack>
          </div>
        )}
        {!loading && results?.length === 0 && !searchTerm && <Body>Provider has no prior authorization cases</Body>}
        {!loading && results && <PAResultList results={results} page={pageNumber} setPage={setPageNumber} />}
      </Stack>
    </div>
  );
};

export default PAListForm;
